import React, { useState } from 'react';
import _ from 'lodash';
import {
  Datagrid,
  TextField,
  FunctionField,
  BooleanInput,
  useResourceContext,
  useRecordContext,
  SimpleShowLayout,
} from 'react-admin';
import LinkField from 'components/LinkField';
import { Button } from '@mui/material';
import { Check, Clear } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import MoneyField from 'components/MoneyField';
import PositionInput from 'components/PositionInput';
import SpecialtyInput from 'components/SpecialtyInput';
import ProRateField from 'components/ProRateField';
import { camelToSnakeCase, DisplayHelpers } from 'utils/helpers';
import List from 'components/List';
import { Box } from '@mui/system';
import LocationTypeInput from 'components/LocationTypeInput';
import {
  calculateTotalPay,
  createRateSettingField,
  getMarginSource,
  getPayoutRate,
  getRateSettingsHash,
  getRateSettingsInfo,
  RateSettingsExportButton,
  useHasLocationTypeFilter,
} from './PositionRateSettingsTab.utils';
import {
  getCreateLinkTo,
  getLinkToAndDisplay,
} from 'utils/rateSettings/PositionRateSettingsTabs.utils';
import { SettingsNestedOperatorSelect } from 'components/SettingsNestedOperatorSelect';
import PercentField from 'components/PercentField';
import {
  RateSettingsDisplayHelper,
  type RateClassificationT,
} from 'utils/displayHelpers/rate-settings';
import { titleize } from 'inflection';
import useCheckAccess from 'hooks/useCheckAccess';
import useGate from 'hooks/useGate';
import { gateNames } from 'constants/statsig';

const Filters = () => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const filters = [
    <PositionInput
      alwaysOn
      source="search.position_ids"
      multiple
      clearable
      filter={{ search: { position_ids: record.position_ids } }}
    />,
    <SpecialtyInput
      alwaysOn
      multiple
      clearable
      source="search.specialty_ids"
      positionSource="search.position_ids"
      optionText="label"
    />,
    <BooleanInput alwaysOn label="Valid For Jobs" source="for_job" />,
    <BooleanInput alwaysOn label="Valid For Assignments" source="for_assignment" />,
  ];

  if (resource === 'Market' || resource === 'State') {
    filters.push(
      <LocationTypeInput alwaysOn source="search.location_type_id" label="Location Type" />,
    );
  }

  return filters;
};

const w2TravelSpecificFields = () => {
  const fields = [
    <MoneyField
      label="Assignment Bonus Tier 2 Travel Amount"
      source="settings.w2.travel.assignment_bonus_tier2_travel_amount_cents"
    />,
    <MoneyField
      label="Assignment Bonus Tier 2 travel Amount TCV"
      source="settings.w2.travel.assignment_bonus_tier2_travel_amount_tcv_cents"
    />,
    <MoneyField
      label="Assignment Bonus Tier 1 Travel Amount"
      source="settings.w2.travel.assignment_bonus_tier1_travel_amount_cents"
    />,
    <MoneyField
      label="Assignment Bonus Tier 1 Travel Amount TCV"
      source="settings.w2.travel.assignment_bonus_tier1_travel_amount_tcv_cents"
    />,
  ];

  return fields;
};

const CreatePositionRateSettingButton = () => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const checkAccess = useCheckAccess();
  const { display, linkTo } = getLinkToAndDisplay(resource, record);
  const action = display === 'Update' ? 'update' : 'create';
  const accessResource =
    resource === 'MarketLocationTypePositionRateSetting'
      ? 'MarketPositionRateSetting'
      : resource === 'StateLocationTypePositionRateSetting'
      ? 'StatePositionRateSetting'
      : resource;

  if (!checkAccess(action, accessResource, 'settings')) {
    return null;
  }

  const stateRecord = _.omit(record, ['settings']);

  return (
    <FunctionField
      render={() => (
        <Button
          component={Link}
          size="small"
          color="primary"
          to={linkTo}
          state={{ record: stateRecord, parentSettings: record.settings }}
        >
          {display}
        </Button>
      )}
    />
  );
};

type PayRateSettingsGridProps = {
  resource: string;
  settingsNestedOperator: string;
  setSettingsNestedOperator: (value: string) => void;
  blockCreateRateSettings: boolean;
  onLocationTypeFilterChange?: (hasLocationType: boolean) => void;
};

export const PayRateSettingsGrid = ({
  resource,
  settingsNestedOperator,
  setSettingsNestedOperator,
  blockCreateRateSettings,
  onLocationTypeFilterChange,
}: PayRateSettingsGridProps) => {
  const hasLocationTypeMarkup = useHasLocationTypeFilter(onLocationTypeFilterChange);

  const parentIdName = `${camelToSnakeCase(resource)}_id`;
  const isW2 = settingsNestedOperator.includes('w2');
  const ratesPrefix = isW2 ? 'W2' : '1099';
  const isMarket = resource === 'Market';
  const isLocation = resource === 'Location';
  const usesLocationTypeMarkup = resource === 'Market' || resource === 'State';

  const locationMarkupProRateLabel = (structure: '8hr' | '12hr') =>
    `${structure}${hasLocationTypeMarkup ? ' Location Markup' : ''} Pro Rate`;

  const getApplicableChargeRateCents = (settings) => {
    const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings });

    return rateSettingsDisplayHelper.getActiveChargeRateCents(
      settingsNestedOperator as RateClassificationT,
    );
  };

  const renderTotalPay = (settings, hours: number, chargeRateCents: number) => {
    const totalPay = calculateTotalPay(
      settings,
      settingsNestedOperator,
      hours,
      'non_blended_rates',
      chargeRateCents,
    );

    if (!totalPay) {
      return;
    }

    return DisplayHelpers.money(totalPay);
  };

  return (
    <>
      <SettingsNestedOperatorSelect
        setSettingsNestedOperator={setSettingsNestedOperator}
        settingsNestedOperator={settingsNestedOperator}
      />
      <Datagrid key={`${ratesPrefix}-${hasLocationTypeMarkup}`} bulkActionButtons={false}>
        {!blockCreateRateSettings && <CreatePositionRateSettingButton />}
        <FunctionField
          label="Active for Job"
          render={(v) => (v?.settings.can_post_job ? <Check /> : <Clear />)}
        />
        <FunctionField
          label="Active for Assignment"
          render={(v) => (v?.settings.can_post_assignment ? <Check /> : <Clear />)}
        />
        <LinkField source={parentIdName} />
        <TextField label="Position" source="position.display_name" />
        <TextField label="Specialty" source="specialty.label" />
        {isMarket && <TextField label="Market" source="market.display_name" />}
        {isLocation && (
          <FunctionField
            label="Active Rate Structure"
            render={({ settings }) => {
              return getRateSettingsHash(settings, settingsNestedOperator)?.use_blended_rate
                ? '12hr'
                : '8hr';
            }}
          />
        )}
        {usesLocationTypeMarkup && (
          <ProRateField
            label={locationMarkupProRateLabel('8hr')}
            nestedOperator={settingsNestedOperator}
            useBlendedRate={false}
            isUsingLocationType={hasLocationTypeMarkup}
            forceChargeRateStructure
          />
        )}
        {usesLocationTypeMarkup && (
          <ProRateField
            label={locationMarkupProRateLabel('12hr')}
            nestedOperator={settingsNestedOperator}
            useBlendedRate={true}
            isUsingLocationType={hasLocationTypeMarkup}
            forceChargeRateStructure
          />
        )}
        {!usesLocationTypeMarkup && (
          <ProRateField
            label={`${!isLocation ? '8hr ' : ''}Pro Rate`}
            nestedOperator={settingsNestedOperator}
            useBlendedRate={false}
            isUsingLocationType={false}
          />
        )}
        {!usesLocationTypeMarkup && !isLocation && (
          <ProRateField
            label="12hr Pro Rate"
            nestedOperator={settingsNestedOperator}
            useBlendedRate={true}
            isUsingLocationType={false}
          />
        )}
        <FunctionField
          label={`Pro${isLocation ? '' : ' 12hr'} Total`}
          render={({ settings }) => {
            const isBlendedRatesActive = getRateSettingsHash(
              settings,
              settingsNestedOperator,
            )?.use_blended_rate;
            const chargeRateCents = getApplicableChargeRateCents(settings);
            if (!chargeRateCents) {
              return;
            }

            if (isBlendedRatesActive) {
              const payoutRate = getPayoutRate(
                settings,
                settingsNestedOperator,
                isLocation ? chargeRateCents : null,
              );
              return DisplayHelpers.money(payoutRate * 12);
            }

            return renderTotalPay(
              settings,
              12,
              isLocation ? chargeRateCents : getApplicableChargeRateCents(settings),
            );
          }}
        />
        <FunctionField
          label={`Pro ${isLocation ? '' : ' 8hr'} w/ Lunch Total`}
          render={({ settings }) => {
            return renderTotalPay(settings, 8, getApplicableChargeRateCents(settings));
          }}
        />
        <FunctionField
          label={`Pro ${isLocation ? '' : ' 8hr'} w/o Lunch Total`}
          render={({ settings }) => {
            return renderTotalPay(settings, 7.5, getApplicableChargeRateCents(settings));
          }}
        />
        {createRateSettingField(
          `${ratesPrefix} Payout Extended Rate`,
          'payout_overtime_multiplier',
          settingsNestedOperator,
        )}
        {createRateSettingField(
          `${ratesPrefix} Payout Second Extended Rate`,
          'payout_double_overtime_multiplier',
          settingsNestedOperator,
        )}
        {createRateSettingField(
          `${ratesPrefix} Payout Extended Rate Starts At`,
          'payout_max_daily_regular_hours',
          settingsNestedOperator,
        )}
        <FunctionField
          label={`${ratesPrefix} Payout Second Extended Rate Starts At`}
          render={({ settings }) => {
            const { rateSettingsPrefix } = getRateSettingsInfo(settings, settingsNestedOperator);
            const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
            const rateSettings = rateSettingsHash?.[rateSettingsPrefix];
            return DisplayHelpers.roundToTwoDecimals(
              rateSettings?.payout_max_daily_regular_hours +
                rateSettings?.payout_max_daily_overtime_hours,
            );
          }}
        />
        <FunctionField
          label={`${ratesPrefix} Payout Weekly Extended Rate Starts At`}
          render={({ settings }) => {
            return DisplayHelpers.roundToTwoDecimals(
              getRateSettingsHash(settings, settingsNestedOperator)
                ?.charge_max_weekly_regular_hours,
            );
          }}
        />
        <FunctionField
          label={`${ratesPrefix} Payout Seventh Day Rule Enabled`}
          render={({ settings }) => {
            return getRateSettingsHash(settings, settingsNestedOperator)
              ?.payout_seventh_day_rule_enabled ? (
              <Check />
            ) : (
              <Clear />
            );
          }}
        />

        {settingsNestedOperator === 'w2.travel' && w2TravelSpecificFields()}

        <PercentField
          label={`Total Margin Percent ${titleize(settingsNestedOperator.replace('.', ' '))}`}
          source={getMarginSource(settingsNestedOperator)}
        />

        <TextField label="Parent" source="id" />
      </Datagrid>
    </>
  );
};

const PayPositionRateSettingsTab = () => {
  const record = useRecordContext();
  const [settingsNestedOperator, setSettingsNestedOperator] = useState(
    record.primary_billing_group?.plan === 'enterprise_w2' ? 'w2.local' : 'ten99',
  );
  const resource = useResourceContext();
  const checkAccess = useCheckAccess();
  const { value: blockCreateRateSettings } = useGate(gateNames.blockCreateNewRateSettings);

  const parentIdName = `${camelToSnakeCase(resource)}_id`;
  const usesLocationTypeMarkup = resource === 'Market' || resource === 'State';
  const [hasLocationType, setHasLocationType] = useState(false);
  const resourceName =
    usesLocationTypeMarkup && hasLocationType
      ? `${resource}LocationTypePositionRateSetting`
      : `${resource}PositionRateSetting`;
  const exportButton = (
    <RateSettingsExportButton
      filenamePrefix={`${record.__typename} ${record.id}`}
      usesLocationTypeMarkup={usesLocationTypeMarkup}
      settingsNestedOperator={settingsNestedOperator}
    />
  );
  const canCreateRateSettings =
    checkAccess(
      'create',
      usesLocationTypeMarkup ? `${resource}PositionRateSetting` : resourceName,
      'settings',
    ) && !blockCreateRateSettings;

  return (
    <SimpleShowLayout>
      {canCreateRateSettings && (
        <Box display="flex">
          <Button
            component={Link}
            color="primary"
            to={getCreateLinkTo(resourceName, resource)}
            state={{ record: { [parentIdName]: record.id } }}
          >
            Add position rate settings
          </Button>
        </Box>
      )}
      <List
        resource={resourceName}
        filters={Filters()}
        hasCreate={false}
        filter={{
          [parentIdName]: record.id,
          include_parents: true,
          position_ids: record.position_ids,
        }}
        exportButton={exportButton}
        disableSyncWithLocation
      >
        <PayRateSettingsGrid
          resource={resource}
          settingsNestedOperator={settingsNestedOperator}
          setSettingsNestedOperator={setSettingsNestedOperator}
          blockCreateRateSettings={blockCreateRateSettings}
          onLocationTypeFilterChange={setHasLocationType}
        />
      </List>
    </SimpleShowLayout>
  );
};

export default PayPositionRateSettingsTab;
